import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { updateProfile } from "firebase/auth";
import {
  FiCamera, FiEdit3, FiMail, FiPhone,
  FiCalendar, FiLogOut, FiShield,
  FiHelpCircle, FiTag, FiCheckCircle, FiStar, FiZap, FiBell
} from 'react-icons/fi';
import { FaBuysellads } from "react-icons/fa";
import { getUserProfile, updateProfileData, uploadProfileImage } from '../../services/ProfileService';
import founderBadge from '../../assets/founders-club-1.png';
import './Profile.css';
import FeaturedCreditsTooltip from '../../components/FeaturedCreditsTooltip/FeaturedCreditsTooltip';
import BoostCreditsTooltip from '../../components/BoostCreditsTooltip/BoostCreditsTooltip';

const ProfilePage = () => {
  const user = auth.currentUser;
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const data = await getUserProfile(user.uid);
      setProfile(data);
      setNewName(data?.username || '');
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSaving(true);
    try {
      const url = await uploadProfileImage(user.uid, file);
      await updateProfile(auth.currentUser, {
        photoURL: url
      });
      await updateProfileData(user.uid, { profile_image_url: url });
      setProfile({ ...profile, profile_image_url: url });
      alert("Profile image updated!");
    } catch (error) {
      alert("Failed to upload image");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateName = async () => {
    if (!newName.trim() || newName === profile.username) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updateProfileData(user.uid, { username: newName.trim() });
      setProfile({ ...profile, username: newName.trim() });
      setIsEditing(false);
    } catch (error) {
      alert("Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loader-full">Loading Profile...</div>;

  const memberSinceLabel = profile?.createdAt?.toDate
    ? profile.createdAt.toDate().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    : 'Recently';

  // Logic: User is verified if they have both Phone (from DB) and Email (from Auth) verified
  const isVerified = user?.emailVerified && profile?.isPhoneVerified;

  return (
    <div className="profile-page-container">
      <div className="profile-header-card">
        <div className="profile-info-main">
          <div className="avatar-wrapper">
            <img
              src={profile?.profile_image_url || `https://ui-avatars.com/api/?name=${profile?.username}&background=8E24AA&color=fff`}
              alt="Profile"
              className={`profile-avatar ${saving ? 'blur' : ''}`}
            />
            <label className="change-photo-badge">
              <input type="file" hidden onChange={handleImageChange} accept="image/*" />
              <FiCamera />
            </label>
          </div>

          <div className="user-details">
            {isEditing ? (
              <div className="edit-name-row">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateName()}
                />
                <button onClick={handleUpdateName} disabled={saving}>Save</button>
                <button onClick={() => setIsEditing(false)} className="cancel">Cancel</button>
              </div>
            ) : (
              <div className="view-name-row">
                <h1>{profile?.username}</h1>
                {isVerified && <FiCheckCircle className="verified-badge-icon" title="Verified User" />}
                {profile?.founder_member && (
                  <img
                    src={founderBadge}
                    alt="Founder"
                    className="founder-badge-icon"
                    title="Founders Club Member"
                  />
                )}
                <FiEdit3 onClick={() => setIsEditing(true)} className="edit-icon" />
              </div>
            )}
            <p className="member-since">
              <FiCalendar /> Member since {memberSinceLabel}
            </p>
          </div>
        </div>

        <div className="contact-badges">
          <div className="badge-item">
            <FiMail /> <span>{profile?.email || user?.email}</span>
          </div>
          <div className="badge-item">
            <FiPhone /> <span>{profile?.phone_number || user?.phoneNumber || 'Not linked'}</span>
          </div>
        </div>
      </div>

      <div className="profile-menu">
        <div className="menu-item my-ads-menu-item" onClick={() => navigate('/my-ads')}>
          <div className="menu-left">
            <FiTag />
            <div className="menu-left-grid">
              <div className="menu-text-content">
                <h4>My Ads</h4>
                <p>Manage your listings and mark items as sold</p>
              </div>

              {/* Featured Credits Badge */}
              {/* <div className="featured-credits-badge">
                <FiStar className="sparkle-icon" style={{width: '18px'}} />
                <div className="featured-credits-text">
                  <span>Featured Ad Credits: <strong>{profile?.benefitsOffered?.freeFeaturedAd?.creditsAvailable || 0}</strong></span>
                </div>
                <FeaturedCreditsTooltip />
              </div> */}

              <div className="user-credits-hub" onClick={(event) => event.stopPropagation()}>
                {/* Boost Credits Badge (Primary MVP Focus) */}
                <div className="credits-badge boost-badge">
                  <FiZap className="zap-icon" style={{ width: '18px' }} />
                  <div className="credits-text">
                    <span>Free Boosts: <strong>{profile?.credits?.boosts?.available || 0}</strong></span>
                  </div>
                  <BoostCreditsTooltip />
                </div>

                {/* Featured Credits Badge (Only shows if they purchased/have some, or as locked) */}
                {profile?.credits?.featured?.available > 0 && (
                  <div className="credits-badge feature-badge">
                    <FiStar className="sparkle-icon" />
                    <div className="credits-text">
                      <span>Featured Credits: <strong>{profile?.credits?.featured?.available || 0}</strong></span>
                    </div>
                    <FeaturedCreditsTooltip />
                  </div>
                )}
              </div>
            </div>
          </div>
          <span className="arrow">→</span>
        </div>

        <div className="menu-item" onClick={() => navigate('/notifications')}>
          <div className="menu-left">
            <FiBell />
            <div>
              <h4>Notifications</h4>
              <p>View your latest alerts and updates</p>
            </div>
          </div>
          <span className="arrow">→</span>
        </div>

        <div className="menu-item" onClick={() => navigate('/claim-ads')}>
          <div className="menu-left">
            <FaBuysellads />
            <div>
              <h4>Claim Reddit Ads</h4>
              <p>Claim and manage ads posted on Reddit</p>
            </div>
          </div>
          <span className="arrow">→</span>
        </div>

        <div className="menu-item" onClick={() => navigate('/account-settings')}>
          <div className="menu-left">
            <FiShield />
            <div>
              <h4>Account & Security</h4>
              <p>Verify email/phone or merge duplicate accounts</p>
            </div>
          </div>
          <span className="arrow">→</span>
        </div>

        <div className="menu-item" onClick={() => navigate('/faq')}>
          <div className="menu-left">
            <FiHelpCircle />
            <div>
              <h4>Help & Support</h4>
              <p>See Faq, Contact us or report a problem</p>
            </div>
          </div>
          <span className="arrow">→</span>
        </div>

        <button className="logout-btn" onClick={() => {
          auth.signOut();
          navigate('/');
        }}>
          <FiLogOut /> Logout from Qinto
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;
